/* Блок «Клиент» быстрой записи: поиск по телефону, госномеру или имени, выбранный клиент с авто, форма нового клиента. */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { CLASSES, ago, dateTime, initials, phoneFmt } from "@okno/shared";
import { api } from "../../lib/api";
import { locKey, useSession } from "../../lib/session";
import type { CustomerRow, SearchHit } from "../../lib/types";
import { Icon } from "../../ui/Icon";
import { Plate } from "../../ui/kit";

export interface NewCustomer {
  name: string;
  phone: string;
  plate: string;
  car: string;
  cls: number;
  sms: boolean;
}

/** Поле поиска получает фокус при открытии модалки (ловушка фокуса ищет [autofocus]). */
const markAutofocus = (el: HTMLInputElement | null) => el?.setAttribute("autofocus", "");

function useDebounced(value: string, ms: number): string {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

interface SearchProps {
  query: string;
  onQuery: (q: string) => void;
  onPick: (hit: SearchHit) => void;
  onNew: () => void;
}

export function ClientSearch({ query, onQuery, onPick, onNew }: SearchProps) {
  const { locationId } = useSession();
  const q = useDebounced(query.trim(), 200);
  const hits = useQuery({
    queryKey: locKey(locationId, "customers-search", q),
    queryFn: () => api.get<SearchHit[]>("/locations/" + locationId + "/customers/search", { q }),
    enabled: q.length >= 2,
    placeholderData: (prev) => prev,
  });
  const results = query.trim().length >= 2 ? hits.data || [] : [];

  return (
    <>
      <label className="field">
        <span>Клиент</span>
        <div className="input-group">
          <Icon name="search" size={16} />
          <input
            ref={markAutofocus}
            className="input"
            placeholder="Телефон, госномер или имя"
            value={query}
            autoComplete="off"
            onChange={(e) => onQuery(e.target.value)}
          />
        </div>
        <span className="hint">Например: 777, 02 или Арман</span>
      </label>
      {results.length ? (
        <div className="panel">
          <div className="list">
            {results.map((r) => {
              const v = r.vehicles.find((x) => x.id === r.matchVehicleId) || r.vehicles[0];
              return (
                <button key={r.customer.id} className="list-item" onClick={() => onPick(r)}>
                  <span className="avatar">{initials(r.customer.name)}</span>
                  <div className="grow">
                    <div className="cell-main">{r.customer.name}</div>
                    <div className="cell-sub">{phoneFmt(r.customer.phone)}</div>
                  </div>
                  {v ? <Plate plate={v.plate} /> : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : query.trim().length >= 2 && q === query.trim() && hits.isFetched && !hits.isFetching ? (
        <p className="muted" style={{ fontSize: 14 }}>
          {hits.isError ? "Не удалось выполнить поиск — попробуйте ещё раз." : "Никого не нашли."}
        </p>
      ) : null}
      <button className="btn btn-sm" style={{ alignSelf: "flex-start" }} onClick={onNew}>
        <Icon name="plus" size={16} />
        Новый клиент
      </button>
    </>
  );
}

interface PickedProps {
  client: CustomerRow;
  vehicleId: string | null;
  onVehicle: (id: string) => void;
  onClear: () => void;
}

export function PickedClient({ client, vehicleId, onVehicle, onClear }: PickedProps) {
  const { customer: c, vehicles, stats } = client;
  return (
    <div className="panel panel-body stack stack-sm">
      <div className="row" style={{ gap: 12 }}>
        <span className="avatar">{initials(c.name)}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="cell-main">{c.name}</div>
          <div className="cell-sub">
            {phoneFmt(c.phone)} · визитов {stats.visits}
            {stats.last ? " · был " + ago(stats.last) : ""}
          </div>
        </div>
        <button className="btn btn-sm btn-ghost" onClick={onClear}>
          Другой клиент
        </button>
      </div>
      <div className="row">
        {vehicles.map((v) => (
          <button key={v.id} className="chip" aria-pressed={v.id === vehicleId} onClick={() => onVehicle(v.id)}>
            {v.plate} · {(v.make + " " + v.model).trim()}
          </button>
        ))}
        {vehicles.length ? null : <span className="muted" style={{ fontSize: 14 }}>У клиента нет автомобилей — добавьте его в карточке клиента.</span>}
      </div>
      {stats.upcoming ? (
        <div className="callout callout-warn">
          <Icon name="info" size={16} />
          <span>У клиента уже есть запись: {dateTime(stats.upcoming.start)}</span>
        </div>
      ) : null}
    </div>
  );
}

interface NewProps {
  value: NewCustomer;
  onChange: (next: NewCustomer) => void;
  onClear: () => void;
}

export function NewClientForm({ value: n, onChange, onClear }: NewProps) {
  const set = <K extends keyof NewCustomer>(k: K, v: NewCustomer[K]) => onChange({ ...n, [k]: v });
  return (
    <div className="panel panel-body stack stack-sm">
      <div className="row row-between">
        <strong>Новый клиент</strong>
        <button className="btn btn-sm btn-ghost" onClick={onClear}>
          Найти в базе
        </button>
      </div>
      <div className="form-grid">
        <label className="field">
          <span>Имя</span>
          <input className="input" value={n.name} autoComplete="off" onChange={(e) => set("name", e.target.value)} />
        </label>
        <label className="field">
          <span>Телефон</span>
          <input className="input" inputMode="tel" placeholder="+7 7__ ___ __ __" value={n.phone} onChange={(e) => set("phone", e.target.value)} />
        </label>
        <label className="field">
          <span>Госномер</span>
          <input className="input" placeholder="123 ABC 02" value={n.plate} onChange={(e) => set("plate", e.target.value)} />
        </label>
        <label className="field">
          <span>Марка и модель</span>
          <input className="input" placeholder="Toyota Camry" value={n.car} onChange={(e) => set("car", e.target.value)} />
        </label>
        <label className="field span-2">
          <span>Класс</span>
          <select className="select" value={n.cls} onChange={(e) => set("cls", +e.target.value)}>
            {CLASSES.map((c, i) => (
              <option key={c} value={i + 1}>
                {c}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="switch">
        <input type="checkbox" checked={n.sms} onChange={(e) => set("sms", e.target.checked)} />
        <span className="track" />
        Согласие на SMS о записи и предложениях
      </label>
    </div>
  );
}
